import {
  assignPlayersToCourtCmd,
  completeSessionScoreCmd,
  joinWaitlistCmd,
  leaveWaitlistCmd,
  reorderWaitlistCmd,
} from './sessionCommands';
import { flushCreateGuestFromOfflineCommand } from './guestService';
import { flushScheduledGameFromOfflineCommand } from './scheduledGamesService';
import { flushPreferenceFromOfflineCommand } from '../utils/session/saveMatchPreferenceMaybeOffline';
import {
  flushPartnershipAddFromOfflineCommand,
  flushPartnershipRemoveFromOfflineCommand,
} from './sessionPartnershipService';
import { addPlayersToSession, removePlayersFromSession, setManualLatePenalty, updateSessionPlayerStatus } from './sessionService';
import {
  deleteCourt,
  restoreOrCreateCourt,
  setSessionUpcomingQueueSlotCount,
} from './sessionStateService';
import { supabase } from '../utils/supabase';
import {
  CONFLICT_COMMAND_CODES,
  OFFLINE_COMMAND_TYPES,
  OFFLINE_NETWORK_TIMEOUT_MS,
  isConflictCommandResult,
  isRetryableCommandResult,
} from '../utils/session/offlineQueueConstants';

/** Own in-flight score during Offline-mode flush — retry, do not treat as another host. */
export const IN_FLIGHT_FLUSH_CODES = new Set([
  'PENDING_OTHER',
  'LOCK_CONTENTION',
  'TRANSITION_ALREADY_PREPARED',
  'STALE_TRANSITION',
]);

export const ALREADY_APPLIED_SEAT_CODES = new Set([
  'SLOT_ALREADY_FILLED',
  'COURT_ALREADY_FILLED',
]);

/** This phone's own log hitting a stale court version — rebase, do not ask about another tab. */
export const OWN_LOG_OCC_CODES = new Set([
  'COURT_CONFLICT',
  'VERSION_MISMATCH',
]);

export const ALREADY_SCORED_CONFLICT_CODES = new Set([
  'MATCH_NOT_ACTIVE',
]);

export const ALREADY_SCORED_MESSAGE =
  'This court was already scored. Offline mode stays on and will skip that save.';

export const COURT_CHANGED_MESSAGE =
  'This court version changed on the server. Offline mode will retry the save.';

export function isAlreadyScoredConflict(result) {
  return ALREADY_SCORED_CONFLICT_CODES.has(result?.code);
}

export function isOtherWriterConflict(blockedReason) {
  return blockedReason === COURT_CHANGED_MESSAGE || blockedReason === ALREADY_SCORED_MESSAGE;
}

export function conflictMessage(result) {
  if (isAlreadyScoredConflict(result)) return ALREADY_SCORED_MESSAGE;
  if (result?.code === 'COURT_CONFLICT' || result?.code === 'VERSION_MISMATCH') {
    return COURT_CHANGED_MESSAGE;
  }
  return result?.message || 'The server rejected this save because the session changed on another device.';
}

export function rebaseHeadCommand(command, conflict = {}) {
  if (!command) return null;
  if (command.type === OFFLINE_COMMAND_TYPES.SEAT) {
    return { ...command, expectedVersion: -1 };
  }
  if (command.type === OFFLINE_COMMAND_TYPES.SCORE) {
    const version = conflict.currentVersion ?? conflict.current_version;
    return {
      ...command,
      // prepare_score_transition skips OCC only when expected version is null, not -1
      expectedCourtVersion: version != null ? version : null,
    };
  }
  return command;
}

export async function isScoreEventAlreadyCommitted(sessionId, scoreEventId) {
  if (!sessionId || !scoreEventId) return false;
  try {
    const { data, error } = await supabase
      .from('session_host_actions')
      .select('id')
      .eq('session_id', sessionId)
      .eq('command_id', scoreEventId)
      .limit(1);
    return !error && Array.isArray(data) && data.length > 0;
  } catch (error) {
    console.warn('[offline-flush] scoreEvent lookup failed:', error);
    return false;
  }
}

export function interpretFlushFailure(command, result, { scoreAlreadyCommitted = false } = {}) {
  if (!result || result.ok) return { ok: true, result };
  if (command?.type === OFFLINE_COMMAND_TYPES.SCORE) {
    if (scoreAlreadyCommitted) return { ok: true, alreadyCommitted: true, result };
    if (IN_FLIGHT_FLUSH_CODES.has(result.code)) {
      return { ok: false, retry: true, message: result.message, result };
    }
    if (OWN_LOG_OCC_CODES.has(result.code)) {
      return { ok: false, retry: true, rebase: true, message: result.message, result };
    }
    if (isAlreadyScoredConflict(result) || CONFLICT_COMMAND_CODES.has(result.code)) {
      return { ok: false, blocked: true, message: conflictMessage(result), result };
    }
    return { ok: false, retry: isRetryableCommandResult(result), message: result.message, result };
  }
  if (command?.type === OFFLINE_COMMAND_TYPES.SEAT) {
    if (
      ALREADY_APPLIED_SEAT_CODES.has(result.code)
      || (scoreAlreadyCommitted && command.causedByScoreEventId)
    ) {
      return { ok: true, alreadyApplied: true, result };
    }
    if (OWN_LOG_OCC_CODES.has(result.code)) {
      return { ok: false, retry: true, rebase: true, message: result.message, result };
    }
    if (isConflictCommandResult(result)) {
      return { ok: false, blocked: true, message: conflictMessage(result), result };
    }
    return { ok: false, retry: isRetryableCommandResult(result), message: result.message, result };
  }
  if (OWN_LOG_OCC_CODES.has(result.code)) {
    return { ok: false, retry: true, rebase: true, message: result.message, result };
  }
  if (isConflictCommandResult(result)) {
    return { ok: false, blocked: true, message: conflictMessage(result), result };
  }
  return { ok: false, retry: isRetryableCommandResult(result), message: result.message, result };
}

export function buildBlockedConflict(flushResult, commandId = null) {
  const result = flushResult?.result || {};
  return {
    message: conflictMessage(result),
    conflict: {
      code: result.code || null,
      currentVersion: result.currentVersion ?? result.current_version ?? null,
      commandId,
    },
  };
}

/**
 * Replay one queued host command with the existing server writes.
 * @returns {{ ok: boolean, blocked?: boolean, retry?: boolean, message?: string, result?: object }}
 */
export async function flushOfflineCommand(command, { sessionId, tabInstanceId, clubId }) {
  if (!command || !sessionId) {
    return { ok: false, retry: true, message: 'Missing command or session' };
  }

  switch (command.type) {
    case OFFLINE_COMMAND_TYPES.SEAT: {
      const seatBody = {
        team1: command.team1 || [],
        team2: command.team2 || [],
        inProgress: command.inProgress === true,
        gameCreation: command.gameCreation || {},
      };
      const expectedVersion = command.overwriteOwnRefill ? -1 : (command.expectedVersion ?? -1);
      let latest = await assignPlayersToCourtCmd(
        sessionId,
        command.index,
        command.courtType,
        expectedVersion,
        seatBody,
        tabInstanceId
      );
      if (latest.ok) return { ok: true, result: latest };
      if (OWN_LOG_OCC_CODES.has(latest.code) || command.overwriteOwnRefill) {
        const retry = await assignPlayersToCourtCmd(
          sessionId,
          command.index,
          command.courtType,
          -1,
          seatBody,
          tabInstanceId
        );
        if (retry.ok) return { ok: true, result: retry };
        latest = retry;
      }
      const scoreAlreadyCommitted = command.causedByScoreEventId
        ? await isScoreEventAlreadyCommitted(sessionId, command.causedByScoreEventId)
        : false;
      return interpretFlushFailure(command, latest, { scoreAlreadyCommitted });
    }

    case OFFLINE_COMMAND_TYPES.SCORE: {
      const scoreBody = {
        sessionId,
        courtNumber: command.courtNumber,
        tabInstanceId,
        team1PlayerIds: command.team1PlayerIds,
        team2PlayerIds: command.team2PlayerIds,
        team1Score: command.team1Score,
        team2Score: command.team2Score,
        winningTeam: command.winningTeam,
        gameTypeId: command.gameTypeId,
        startTime: command.startTime,
        endTime: command.endTime,
        scoreEventId: command.scoreEventId,
        gameCreateMethod: command.gameCreateMethod,
        matchmakingAlgorithm: command.matchmakingAlgorithm || 'two_stage_local',
        scheduledGameId: command.scheduledGameId,
        eloUpdates: command.eloUpdates,
        expectedCourtVersion: command.expectedCourtVersion,
      };
      let latest = await completeSessionScoreCmd(scoreBody);
      if (latest.ok) return { ok: true, result: latest };
      const scoreAlreadyCommitted = command.scoreEventId
        ? await isScoreEventAlreadyCommitted(sessionId, command.scoreEventId)
        : false;
      if (scoreAlreadyCommitted) return { ok: true, alreadyCommitted: true, result: latest };
      if (OWN_LOG_OCC_CODES.has(latest.code)) {
        const retry = await completeSessionScoreCmd({
          ...scoreBody,
          expectedCourtVersion: null,
        });
        if (retry.ok) return { ok: true, result: retry };
        latest = retry;
      }
      return interpretFlushFailure(command, latest, { scoreAlreadyCommitted });
    }

    case OFFLINE_COMMAND_TYPES.JOIN_WAITLIST: {
      const result = await joinWaitlistCmd(sessionId, command.playerId, tabInstanceId);
      if (result.ok) return { ok: true, result };
      return {
        ok: false,
        blocked: isConflictCommandResult(result),
        retry: isRetryableCommandResult(result),
        message: result.message,
        result,
      };
    }

    case OFFLINE_COMMAND_TYPES.LEAVE_WAITLIST: {
      const result = await leaveWaitlistCmd(sessionId, command.playerId, tabInstanceId);
      if (result.ok) return { ok: true, result };
      return {
        ok: false,
        blocked: isConflictCommandResult(result),
        retry: isRetryableCommandResult(result),
        message: result.message,
        result,
      };
    }

    case OFFLINE_COMMAND_TYPES.REORDER_WAITLIST: {
      const result = await reorderWaitlistCmd(sessionId, command.orderedPlayerIds, tabInstanceId);
      if (result.ok) return { ok: true, result };
      return {
        ok: false,
        blocked: isConflictCommandResult(result),
        retry: isRetryableCommandResult(result),
        message: result.message,
        result,
      };
    }

    case OFFLINE_COMMAND_TYPES.ADD_PLAYER: {
      try {
        await addPlayersToSession(sessionId, [command.playerId], clubId, command.status || 'active');
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.CREATE_GUEST: {
      try {
        await flushCreateGuestFromOfflineCommand(command, { sessionId, clubId });
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.PLAYER_STATUS: {
      try {
        await updateSessionPlayerStatus(sessionId, command.playerId, command.status, command.exitReason || null);
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.REMOVE_PLAYER: {
      try {
        await removePlayersFromSession(sessionId, [command.playerId]);
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.LATE_PENALTY: {
      try {
        await setManualLatePenalty(sessionId, command.playerId, command.penalty ?? 0);
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.COURT_COUNT: {
      try {
        if (command.target === 'upcoming') {
          await setSessionUpcomingQueueSlotCount(sessionId, command.newCount);
          return { ok: true };
        }
        if (command.target === 'active') {
          for (const courtNumber of command.courtNumbersAdded || []) {
            await restoreOrCreateCourt(sessionId, courtNumber);
          }
          if (command.courtNumberRemoved != null) {
            await deleteCourt(sessionId, command.courtNumberRemoved);
          }
          return { ok: true };
        }
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.PARTNERSHIP_ADD: {
      try {
        await flushPartnershipAddFromOfflineCommand(command, { sessionId });
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.PARTNERSHIP_REMOVE: {
      try {
        await flushPartnershipRemoveFromOfflineCommand(command, { sessionId });
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.PREFERENCE: {
      try {
        await flushPreferenceFromOfflineCommand(command, { sessionId });
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    case OFFLINE_COMMAND_TYPES.SCHEDULED_GAME: {
      try {
        await flushScheduledGameFromOfflineCommand(command, { sessionId, clubId });
        return { ok: true };
      } catch (error) {
        return { ok: false, retry: true, message: error.message };
      }
    }

    default:
      return { ok: true };
  }
}

/**
 * Flush one command, but stop waiting after the live-session timeout.
 * The original request can still finish later; the caller should leave the command queued.
 */
export async function flushOfflineCommandWithBudget(
  command,
  ctx,
  timeoutMs = OFFLINE_NETWORK_TIMEOUT_MS
) {
  let timeoutId = null;
  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(
      () => resolve({ ok: false, retry: true, timedOut: true, message: 'Save timed out' }),
      timeoutMs
    );
  });
  try {
    return await Promise.race([flushOfflineCommand(command, ctx), timeoutPromise]);
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }
}
